import React from "react";
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { Tooltip } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { format } from "date-fns";
import { numberFormat } from "../../util/currency-formatter";
import { toDecimal, roundValue } from "../../util/decimal-utils";
import '../sync-contacts-modal/sync-contacts-modal.scss';

export const getSyncInvoicesColumns = ({
    invoices,
    selectedIds,
    syncing,
    handleSelectAll,
    handleSelectRow,
    entityType,
    propProjectId,
    billTab,
    syncLogs
}) => {
    const columns = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    checked={invoices.length > 0 && selectedIds.length === invoices.length}
                    onChange={handleSelectAll}
                    disabled={syncing}
                    data-cy="sync-invoices-select-all"
                />
            ),
            field: "select",
            class: "text-center table-select-col",
            render: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                    disabled={syncing}
                    data-cy={`sync-invoices-select-${row.id}`}
                />
            )
        }
    ];

    if (entityType === 'invoice') {
        if (!propProjectId) {
            columns.push({ title: "Project", field: "projectName", render: (row) => row.projectName || '-' });
        }
        columns.push(
            { title: "Claim Number", field: "claimNumber", render: (row) => row.claimNumber || '-' },
            { title: "Date", field: "date", render: (row) => row.date ? format(new Date(row.date), 'dd/MM/yyyy') : '—' },
            { title: "Invoice Number", field: "invoiceNumber", render: (row) => <strong>{row.invoiceNumber || '-'}</strong> },
            { title: "Retention Held", field: "retentionAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionAmount || 0) },
            { title: "Retention Released", field: "retentionClaimAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionClaimAmount || 0) },
            {
                title: "Amount",
                field: "approved",
                class: "text-end fw-bold",
                render: (row) => numberFormat(row.approved || 0)
            },
            {
                title: "Amount (Incl. GST)",
                field: "amountInclGst",
                class: "text-end fw-bold",
                render: (row) => {
                    const val = row.amountInclGst !== undefined && row.amountInclGst !== null
                        ? row.amountInclGst
                        : (row.totalAmount !== undefined && row.totalAmount !== null
                            ? row.totalAmount
                            : (row.total !== undefined && row.total !== null
                                ? row.total
                                : (row.grossAmount !== undefined && row.grossAmount !== null
                                    ? row.grossAmount
                                    : roundValue(toDecimal(row.approved || 0).times(toDecimal(1.1)), 2))));
                    return numberFormat(val);
                }
            },
            {
                title: "Status",
                field: "status",
                render: (row) => (
                    <span className={`badge ${row.status === 'APPROVED' ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {row.status}
                    </span>
                )
            }
        );
    } else {
        if (!propProjectId) {
            columns.push({ title: "Project", field: "projectName", render: (row) => row.projectName || '-' });
        }
        columns.push(
            { title: "Ref Number", field: "reference", render: (row) => <strong>{row.reference || '-'}</strong> },
            { title: "PO Ref", field: "poNumber", render: (row) => <span className="text-secondary small">{row.fId || row.poNumber || '-'}</span> },
            { title: billTab === 'contractor' ? "Contractor" : "Supplier", field: "vendorName", render: (row) => <span className="text-truncate d-inline-block text-truncate-max">{row.vendorName}</span> },
            { title: "Date", field: "date", render: (row) => <span className="small">{row.date ? format(new Date(row.date), 'dd/MM/yyyy') : '—'}</span> }
        );
        if (billTab === 'contractor') {
            columns.push(
                { title: "Retention Held", field: "retentionAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionAmount) },
                { title: "Retention Released", field: "retentionReleasedAmount", class: "text-end fw-bold", render: (row) => numberFormat(row.retentionReleasedAmount) }
            );
        }
        columns.push(
            { title: "Amount", field: "amount", class: "text-end fw-bold", render: (row) => numberFormat(row.amount) },
            {
                title: "Amount (Incl. GST)",
                field: "amountInclGst",
                class: "text-end fw-bold",
                render: (row) => {
                    const val = row.amountInclGst !== undefined && row.amountInclGst !== null
                        ? row.amountInclGst
                        : (row.totalAmount !== undefined && row.totalAmount !== null
                            ? row.totalAmount
                            : (row.total !== undefined && row.total !== null
                                ? row.total
                                : (row.grossAmount !== undefined && row.grossAmount !== null
                                    ? row.grossAmount
                                    : roundValue(toDecimal(row.amount || 0).times(toDecimal(1.1)), 2))));
                    return numberFormat(val);
                }
            }
        );
    }

    if (Object.keys(syncLogs).length > 0) {
        columns.push({
            title: "Push Status",
            field: "pushStatus",
            class: "text-center align-middle",
            render: (row) => {
                const log = syncLogs[row.id];
                if (!log) return null;
                return (
                    <>
                        {log.status === 'pending' && (
                            <Tooltip
                                title="Queued"
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <HourglassEmptyIcon className="sync-status-icon sync-status-icon--pending" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: '#6c757d' }} />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'syncing' && (
                            <Tooltip
                                title="Pushing to accounting platform..."
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <Spinner animation="border" size="sm" className="text-primary sync-spinner-sm" style={{ width: '0.875rem', height: '0.875rem' }} />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'success' && (
                            <Tooltip
                                title="Synced Successfully"
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{ style: { zIndex: 99999 } }}
                            >
                                <span>
                                    <CheckCircleIcon className="sync-status-icon sync-status-icon--success" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: '#198754' }} />
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'error' && (
                            <Tooltip
                                title={<div className="sync-error-tooltip-content">{log.msg}</div>}
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{
                                    style: { zIndex: 99999 }
                                }}
                            >
                                <span className="d-inline-flex align-items-center sync-error-link text-danger">
                                    <CancelIcon className="sync-status-icon sync-status-icon--error" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: 'inherit', verticalAlign: 'middle' }} />
                                    <span className="ms-1 small fw-semibold" style={{ color: 'inherit' }}>View Error</span>
                                </span>
                            </Tooltip>
                        )}
                        {log.status === 'cancelled' && (
                            <Tooltip
                                title={<div className="sync-error-tooltip-content">{log.msg || 'Cancelled before processing'}</div>}
                                placement="top"
                                arrow
                                followCursor={true}
                                PopperProps={{
                                    style: { zIndex: 99999 }
                                }}
                            >
                                <span className="d-inline-flex align-items-center">
                                    <DoNotDisturbIcon className="sync-status-icon" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: '#f59e0b', verticalAlign: 'middle' }} />
                                    <span className="ms-1 small fw-semibold" style={{ color: '#f59e0b' }}>Cancelled</span>
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            }
        });
    }

    return columns;
};
